import { getMinuteWithSecond, MinuteWithSecond } from '@collector/shared-utils'
import { IncidentId } from '@collector/sportsapi-client-legacy'

import { injectEventClock, injectEventClockHelpers } from '../../provide-inject'
import { useDuration } from './useDuration'

export function useIncidentClock(): {
  createIncidentTime(incidentId: IncidentId): MinuteWithSecond
} {
  const eventClock = injectEventClock()
  const eventClockHelpers = injectEventClockHelpers()
  const { getDurationSeconds, getIncidentDuration, getEventStatusDuration } =
    useDuration()

  /**
   * Creates a time for an incident based on the current time on the clock
   *
   * Returned value should be added to an incident payload, when a new incident is sent to the API
   *
   * For a sport which:
   * - doesn't have a clock, it should return `null`
   * - has a clock but no concept of duration or no duration for event current status,
   *   it should return the current time on the clock
   * - has a clock and a defined duration, it should return a custom time — "absolute time" in the event
   *
   * Based on `getAbsoluteEventTime`
   *
   * https://github.com/statscore/sportsadmin/blob/master/resources/src-js/protocols/timer/_base.pure.game.time.duration.calculator.js#L37
   */
  function createIncidentTime(incidentId: IncidentId): MinuteWithSecond {
    if (!eventClock) {
      return { minute: 0, second: 0 }
    }

    // 1. calculate time using duration associated with `incidentId` (without current clock time)
    const incidentDuration = getIncidentDuration(incidentId)
    if (incidentDuration) {
      return getMinuteWithSecond(getDurationSeconds(incidentDuration))
    }

    // 2. calculate time using duration associated with current event status
    const eventStatusDuration = getEventStatusDuration()
    if (eventStatusDuration) {
      const { minute, second } = eventClock.eventTime.value
      const clockTime = eventClockHelpers!.getEventClockTime(
        minute,
        second,
        eventClockHelpers!.currentPeriodStartOffsetMinutes.value,
        eventClockHelpers!.isTimerDirectionDown.value,
      )

      const periodStartOffsetSeconds =
        (eventClockHelpers!.currentPeriodStartOffsetMinutes.value ?? 0) * 60

      const currentTimeSeconds =
        getDurationSeconds(eventStatusDuration) +
        periodStartOffsetSeconds +
        clockTime

      return getMinuteWithSecond(currentTimeSeconds)
    }

    // 3. current time on the clock
    return eventClock.eventTime.value
  }

  return { createIncidentTime }
}
